import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';

/**
 * Compact on-chart trading widget for the Market page.
 *
 * Fires market orders against the user's "primary account" (or the
 * override account they pick in the dropdown). Sits below every chart.
 *
 * Cross-platform — backend resolves the right execution path by
 * `platform_kind` (metaapi, tradelocker, bybit, volumetrica, funded_native).
 */
export function ChartTradePanel({ symbol, lastPrice, onArmPending, forcedAccount }) {
  const [accounts, setAccounts]   = useState([]);
  const [primary, setPrimary]     = useState(null);
  const [override, setOverride]   = useState(null);   // {platform_kind, connection_id}
  const [size, setSize]           = useState('0.01');
  const [sl, setSl]               = useState('');
  const [tp, setTp]               = useState('');
  const [busy, setBusy]           = useState(false);
  const [orderMode, setOrderMode] = useState('market');  // 'market' | 'limit' | 'stop'

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ accounts: tradeables = [] }, p] = await Promise.all([
          api.getTradeableAccounts(),
          api.getPrimaryAccount(),
        ]);
        if (!alive) return;
        setAccounts(tradeables);
        setPrimary(p);
      } catch (e) { /* silent — chart should still render */ }
    })();
    return () => { alive = false; };
  }, []);

  // When the parent (MarketPage) forces a target account because the user
  // selected one of their personal strategies, override locally — but allow
  // the trader to switch via the dropdown if they want.
  useEffect(() => {
    if (forcedAccount && forcedAccount.connection_id) {
      setOverride({ platform_kind: forcedAccount.platform_kind, connection_id: forcedAccount.connection_id });
    }
  }, [forcedAccount?.connection_id, forcedAccount?.platform_kind]);

  const target = override || primary || (forcedAccount && forcedAccount.connection_id ? forcedAccount : null);
  const targetAcct = target && accounts.find(a =>
    a.platform_kind === target.platform_kind && a.connection_id === target.connection_id
  );

  const submit = async (direction) => {
    const n = parseFloat(size);
    if (!Number.isFinite(n) || n <= 0) { toast.error('Enter a valid size'); return; }
    if (!target) { toast.error('Pick a trading account first'); return; }

    // Pending order modes use a different endpoint + need a trigger price
    if (orderMode !== 'market') {
      if (onArmPending) {
        onArmPending({
          mode: orderMode,
          direction,
          size: n,
          sl: sl === '' ? null : parseFloat(sl),
          tp: tp === '' ? null : parseFloat(tp),
          override: override || null,
        });
        toast.info(`${orderMode.toUpperCase()} ${direction} armed — click the chart at your trigger price`);
      }
      return;
    }

    setBusy(true);
    try {
      const payload = {
        symbol, direction, size: n,
        sl: sl === '' ? null : parseFloat(sl),
        tp: tp === '' ? null : parseFloat(tp),
        override_account: override || undefined,
      };
      const r = await api.chartPlaceOrder(payload);
      const label = targetAcct?.label || target.platform_kind;
      if (r.execution_mode === 'semi_auto') {
        toast.success(`${direction} ${symbol} queued — check ${label} to confirm`);
      } else {
        toast.success(`${direction} ${symbol} fired on ${label}`);
      }
    } catch (e) {
      toast.error(e.message || 'Order failed');
    }
    setBusy(false);
  };

  // No linked accounts at all? Render a soft prompt instead of trade buttons —
  // unless a strategy has forced a specific account (then proceed; the order
  // will be routed by platform_kind regardless of the tradeable-accounts list).
  if (accounts.length === 0 && !forcedAccount) {
    return (
      <div data-testid="chart-trade-empty" style={{ border: '1px dashed var(--pp-border)', padding: 14, fontSize: 12, color: 'var(--pp-text-3)' }}>
        Link a broker (Profile → Connections) to trade {symbol} directly from this chart.
      </div>
    );
  }

  return (
    <div data-testid="chart-trade-panel" style={{ border: '1px solid var(--pp-border)', padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, letterSpacing: '0.12em', color: 'var(--pp-text-3)' }}>TRADE ON:</span>
        <select
          data-testid="chart-trade-account-select"
          value={target ? `${target.platform_kind}:${target.connection_id}` : ''}
          onChange={e => {
            const v = e.target.value;
            if (!v) { setOverride(null); return; }
            const [pk, cid] = v.split(':');
            setOverride({ platform_kind: pk, connection_id: cid });
          }}
          style={{ flex: 1, minWidth: 180, padding: '6px 8px', background: 'transparent', color: 'var(--pp-text)', border: '1px solid var(--pp-border)', fontSize: 12 }}
        >
          <option value="">— pick account —</option>
          {accounts.map(a => (
            <option key={`${a.platform_kind}:${a.connection_id}`} value={`${a.platform_kind}:${a.connection_id}`}>
              {a.label} ({a.platform_kind === 'funded_native' ? 'native sim' : a.platform_kind})
            </option>
          ))}
        </select>
        {target && primary && (target.platform_kind !== primary.platform_kind || target.connection_id !== primary.connection_id) && (
          <button
            data-testid="chart-trade-make-primary-btn"
            onClick={async () => {
              try {
                await api.setPrimaryAccount({ platform_kind: target.platform_kind, connection_id: target.connection_id });
                setPrimary(target);
                setOverride(null);
                toast.success('Primary account updated');
              } catch (e) { toast.error(e.message); }
            }}
            style={{ fontSize: 10, padding: '4px 8px', background: 'transparent', border: '1px solid var(--pp-primary)', color: 'var(--pp-primary)', cursor: 'pointer', letterSpacing: '0.08em', fontWeight: 700 }}>
            ⭐ MAKE PRIMARY
          </button>
        )}
      </div>

      {/* Order type selector — market vs pending limit/stop */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {['market', 'limit', 'stop'].map(m => (
          <button
            key={m}
            data-testid={`chart-trade-mode-${m}`}
            onClick={() => setOrderMode(m)}
            style={{ flex: 1, padding: '6px 0', background: orderMode === m ? 'var(--pp-overlay)' : 'transparent', border: `1px solid ${orderMode === m ? 'var(--pp-primary)' : 'var(--pp-border)'}`, color: orderMode === m ? 'var(--pp-text)' : 'var(--pp-text-3)', fontSize: 11, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}>
            {m.toUpperCase()}
          </button>
        ))}
      </div>
      {orderMode !== 'market' && (
        <p style={{ margin: '0 0 8px', fontSize: 10, color: 'var(--pp-text-3)' }}>
          Press BUY or SELL to arm, then <strong>click the chart</strong> at your trigger price.
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
        <input
          data-testid="chart-trade-size-input"
          type="number" step="0.01" min="0.01"
          value={size}
          onChange={e => setSize(e.target.value)}
          placeholder="Size"
          style={{ padding: '8px', background: 'transparent', color: 'var(--pp-text)', border: '1px solid var(--pp-border)', fontSize: 13 }}
        />
        <input
          data-testid="chart-trade-sl-input"
          type="number" step="any"
          value={sl}
          onChange={e => setSl(e.target.value)}
          placeholder={`SL (${lastPrice ? '@' + lastPrice : 'optional'})`}
          style={{ padding: '8px', background: 'transparent', color: 'var(--pp-text)', border: '1px solid var(--pp-border)', fontSize: 13 }}
        />
        <input
          data-testid="chart-trade-tp-input"
          type="number" step="any"
          value={tp}
          onChange={e => setTp(e.target.value)}
          placeholder="TP (optional)"
          style={{ padding: '8px', background: 'transparent', color: 'var(--pp-text)', border: '1px solid var(--pp-border)', fontSize: 13 }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <button
          data-testid="chart-trade-buy-btn"
          disabled={busy || !target}
          onClick={() => submit('BUY')}
          style={{ padding: '12px 0', background: '#00FF94', color: '#000', border: 'none', fontWeight: 800, letterSpacing: '0.12em', fontSize: 13, cursor: busy || !target ? 'not-allowed' : 'pointer', opacity: busy || !target ? 0.5 : 1 }}>
          ▲ BUY {symbol}
        </button>
        <button
          data-testid="chart-trade-sell-btn"
          disabled={busy || !target}
          onClick={() => submit('SELL')}
          style={{ padding: '12px 0', background: '#FF5470', color: '#fff', border: 'none', fontWeight: 800, letterSpacing: '0.12em', fontSize: 13, cursor: busy || !target ? 'not-allowed' : 'pointer', opacity: busy || !target ? 0.5 : 1 }}>
          ▼ SELL {symbol}
        </button>
      </div>

      {targetAcct?.platform_kind === 'volumetrica' && (
        <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
          Volumetrica fires automatically — no manual confirm needed.
          {(sl !== '' || tp !== '') && (
            <> SL/TP are queued and placed as separate pending orders <strong>after</strong> the entry fills. One leg filling auto-cancels the other.</>
          )}
        </p>
      )}
    </div>
  );
}
